import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Award } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { analyticsAPI } from '../../../utils/api';
import { getErrorMessage } from '../../../utils/errorTranslation';

const QualityMetrics = ({ filters, showToast }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getQualityMetrics({
        period: filters.period,
        offerId: filters.offerId
      });
      setData(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching quality metrics:', error);
      if (showToast) {
        showToast(getErrorMessage(error, t, t('dashboard.analytics.error.fetchFailed')), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [filters.offerId, filters.period, showToast, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 h-full min-h-[320px]"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.analytics.quality.title')}</h3>
          <p className="text-sm text-gray-500">{t('dashboard.analytics.quality.subtitle')}</p>
        </div>
        <Award className="h-6 w-6 text-primary-600" />
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : !data ? (
        <div className="text-sm text-gray-500">{t('dashboard.analytics.quality.noData')}</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">{t('dashboard.analytics.quality.avgScore')}</div>
              <div className="text-lg font-semibold text-gray-900">{data.avgScore || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">{t('dashboard.analytics.quality.highScore')}</div>
              <div className="text-lg font-semibold text-gray-900">{data.highScorePercentage || 0}%</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">{t('dashboard.analytics.quality.candidates')}</div>
              <div className="text-lg font-semibold text-gray-900">{data.totalScored || 0}</div>
            </div>
          </div>

          {Array.isArray(data.distribution) && data.distribution.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('dashboard.analytics.quality.distribution')}</h4>
              <div className="space-y-1 text-sm text-gray-600">
                {data.distribution.map((range) => (
                  <div key={range.label} className="flex items-center justify-between">
                    <span>{range.label}</span>
                    <span className="font-medium">{range.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default QualityMetrics;
